import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { FocusModeType, FocusModeConfig, UrgentMessageRequest, DEEP_WORK_QUOTES } from '@/types/mindfulness';
import { authFetch, buildApiUrl } from '@/config/api';
import { QUOTE_ROTATION_INTERVAL } from '@/lib/constants';
import { useNotificationsStore } from '@/stores/useNotificationsStore';
import { useChannelsStore } from '@/stores/useChannelsStore';
import { useUIStore } from '@/stores/useUIStore';
import { useAuth } from './AuthContext';
import { storeFocusSessionSummary } from '@/utils/focusSessionStorage';

interface MindfulnessContextType {
  // Current focus mode state
  focusMode: FocusModeType;
  focusConfig: FocusModeConfig;
  
  // Mode switching
  setFocusMode: (mode: FocusModeType, config?: Partial<FocusModeConfig>) => void;
  exitFocusMode: () => void;
  
  // Focus mode channel selection
  focusChannelIds: string[];
  setFocusChannelIds: (channelIds: string[]) => void;
  
  // Deep work state
  isDeepWorkActive: boolean;
  currentQuote: { text: string; author: string };
  refreshQuote: () => void;
  
  // Urgent messages queue (for deep work mode)
  urgentMessages: UrgentMessageRequest[];
  addUrgentMessage: (message: UrgentMessageRequest) => void;
  dismissUrgentMessage: (messageId: string) => void;
  clearUrgentMessages: () => void;
  
  // UI state
  isModeSelectorOpen: boolean;
  setModeSelectorOpen: (open: boolean) => void;
  isFocusSetupOpen: boolean;
  setFocusSetupOpen: (open: boolean) => void;
  showFocusSummary: boolean;
  dismissFocusSummary: () => void;
  exitingFocusDuration?: string;
  exitingFocusStartTime?: string;
  
  // Helpers
  isChannelAccessible: (channelId: string) => boolean;
  shouldShowNotification: (isMention: boolean, channelId?: string) => boolean;
  
  // SignalR sync
  syncFromSignalR: (mode: FocusModeType, activatedAt: string | null) => void;
}

const MindfulnessContext = createContext<MindfulnessContextType | undefined>(undefined);

const STORAGE_KEY = 'cleariest_mindfulness_state';

function getRandomQuote() {
  return DEEP_WORK_QUOTES[Math.floor(Math.random() * DEEP_WORK_QUOTES.length)];
}

export function MindfulnessProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [focusMode, setFocusModeState] = useState<FocusModeType>('collaboration');
  const [focusConfig, setFocusConfig] = useState<FocusModeConfig>({ mode: 'collaboration' });
  const [focusChannelIds, setFocusChannelIdsState] = useState<string[]>([]);
  const [urgentMessages, setUrgentMessages] = useState<UrgentMessageRequest[]>([]);
  const [isModeSelectorOpen, setModeSelectorOpen] = useState(false);
  const [isFocusSetupOpen, setFocusSetupOpen] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(getRandomQuote());
  const [isLoading, setIsLoading] = useState(true);
  const isSyncing = useRef(false);
  const [showFocusSummary, setShowFocusSummary] = useState(false);
  const [exitingFocusDuration, setExitingFocusDuration] = useState<string | undefined>();
  const [exitingFocusStartTime, setExitingFocusStartTime] = useState<string | undefined>();

  // Load state from backend on mount - ONLY if authenticated
  useEffect(() => {
    // Wait for auth to be ready
    if (authLoading) {
      return;
    }

    // If not authenticated, just use defaults and mark as loaded
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    const loadFromBackend = async () => {
      try {
        const response = await authFetch(buildApiUrl('/api/focusmode'));
        if (response.ok) {
          const data = await response.json();
          setFocusModeState(data.mode as FocusModeType || 'collaboration');
          setFocusChannelIdsState(data.channelIds || []);
          setFocusConfig({
            mode: data.mode as FocusModeType || 'collaboration',
            selectedChannelIds: data.channelIds || [],
            activatedAt: data.activatedAt,
          });
        }
      } catch (e) {
        console.error('Failed to load focus mode from backend:', e);
        // Fall back to localStorage
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            const storedMode = parsed.focusMode || 'collaboration';
            const storedConfig = parsed.focusConfig || { mode: 'collaboration' };
            const hasValidActivation = Boolean(storedConfig?.activatedAt);

            // Avoid getting stuck in deep work if we don't have a valid activation timestamp
            if ((storedMode === 'deep-work' || storedMode === 'focus') && !hasValidActivation) {
              setFocusModeState('collaboration');
              setFocusConfig({ mode: 'collaboration' });
              setFocusChannelIdsState([]);
            } else {
              setFocusModeState(storedMode);
              setFocusConfig(storedConfig);
              setFocusChannelIdsState(parsed.focusChannelIds || []);
            }
          } catch (parseError) {
            console.error('Failed to parse local mindfulness state:', parseError);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadFromBackend();
  }, [isAuthenticated, authLoading]);

  // Persist state to localStorage as backup
  useEffect(() => {
    if (isLoading) return;
    const state = {
      focusMode,
      focusConfig,
      focusChannelIds,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [focusMode, focusConfig, focusChannelIds, isLoading]);

  // Sync to backend helper
  const syncToBackend = useCallback(async (mode: FocusModeType, channelIds: string[]) => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    
    try {
      await authFetch(buildApiUrl('/api/focusmode'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          channelIds,
        }),
      });
    } catch (e) {
      console.error('Failed to sync focus mode to backend:', e);
    } finally {
      isSyncing.current = false;
    }
  }, []);

  // Refresh quote periodically in deep work mode
  useEffect(() => {
    if (focusMode === 'deep-work') {
      const interval = setInterval(() => {
        setCurrentQuote(getRandomQuote());
      }, QUOTE_ROTATION_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [focusMode]);

  const calculateFocusDuration = useCallback(() => {
    if (!focusConfig.activatedAt) return undefined;
    
    const start = new Date(focusConfig.activatedAt).getTime();
    const now = Date.now();
    const diff = Math.floor((now - start) / 1000);
    
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }, [focusConfig.activatedAt]);

  const setFocusMode = useCallback(async (mode: FocusModeType, config?: Partial<FocusModeConfig>) => {
    const previousMode = focusMode;
    
    // Check if we're exiting to collaboration mode from focus or deep-work
    const isExitingToCollaboration = mode === 'collaboration' && (previousMode === 'focus' || previousMode === 'deep-work');
    
    if (isExitingToCollaboration) {
      // Calculate duration before changing mode
      const duration = calculateFocusDuration();
      
      // Sync to collaboration mode on backend first
      await syncToBackend('collaboration', []);
      
      // Fetch fresh notifications
      const fetchSummary = useNotificationsStore.getState().fetchSummary;
      await fetchSummary();
      
      // Update state
      setFocusModeState(mode);
      setFocusConfig({ mode: 'collaboration' });
      setFocusChannelIdsState([]);
      setUrgentMessages([]);
      
      // Show summary screen
      setExitingFocusDuration(duration);
      setShowFocusSummary(true);
    } else {
      // Normal mode switching
      const newConfig: FocusModeConfig = {
        mode,
        activatedAt: new Date().toISOString(),
        ...config,
      };
      
      setFocusModeState(mode);
      setFocusConfig(newConfig);
      
      // Sync to backend
      const channelIds = config?.selectedChannelIds || focusChannelIds;
      syncToBackend(mode, channelIds);
      
      // Reset urgent messages when entering deep work
      if (mode === 'deep-work') {
        setUrgentMessages([]);
        setCurrentQuote(getRandomQuote());
      }
    }
  }, [focusMode, focusChannelIds, syncToBackend, calculateFocusDuration]);

  const exitFocusMode = useCallback(async () => {
    // Store summary for both focus and deep-work modes
    if (focusMode === 'focus' || focusMode === 'deep-work') {
      const duration = calculateFocusDuration();
      
      // Switch to collaboration mode on backend FIRST
      await syncToBackend('collaboration', []);
      
      // THEN fetch the AI summary with the new mode active
      const fetchSummary = useNotificationsStore.getState().fetchSummary;
      
      try {
        // Get the AI summary from backend
        const url = buildApiUrl('/api/focusmode/summary' + (focusConfig.activatedAt ? `?focusStartTime=${encodeURIComponent(focusConfig.activatedAt)}` : ''));
        const response = await authFetch(url);
        
        if (response.ok) {
          const data = await response.json();
          
          // Store in localStorage for the 4th tab
          storeFocusSessionSummary({
            summary: data.summary,
            messageCount: data.messageCount,
            focusStartTime: data.focusStartTime,
            channelCount: data.channelCount,
            focusDuration: duration,
            focusMode: focusMode as 'focus' | 'deep-work',
          });
          
          // Dispatch event to notify Dashboard component
          window.dispatchEvent(new Event('focusSessionSummaryChanged'));
          
          // Navigate to Dashboard by clearing channel/DM selection
          useChannelsStore.getState().clearSelection();
          useUIStore.getState().showWelcomeScreen();
        }
        
        // Fetch fresh notifications
        await fetchSummary();
      } catch (error) {
        console.error('Failed to fetch focus summary:', error);
      }
      
      // Clean up local state
      setFocusModeState('collaboration');
      setFocusConfig({ mode: 'collaboration' });
      setFocusChannelIdsState([]);
      setUrgentMessages([]);
    } else {
      // For collaboration mode, just clean up
      setFocusModeState('collaboration');
      setFocusConfig({ mode: 'collaboration' });
      setFocusChannelIdsState([]);
      setUrgentMessages([]);
      await syncToBackend('collaboration', []);
    }
  }, [focusMode, syncToBackend, calculateFocusDuration, focusConfig.activatedAt]);

  const dismissFocusSummary = useCallback(async () => {
    // This is now only used for legacy compatibility - the summary is shown in Dashboard
    setShowFocusSummary(false);
    setExitingFocusStartTime(undefined);
  }, []);

  const setFocusChannelIds = useCallback((channelIds: string[]) => {
    // Limit to 3 channels
    const limited = channelIds.slice(0, 3);
    setFocusChannelIdsState(limited);
    if (focusMode === 'focus') {
      setFocusConfig(prev => ({
        ...prev,
        selectedChannelIds: limited,
      }));
      // Sync to backend when channel selection changes in focus mode
      syncToBackend(focusMode, limited);
    }
  }, [focusMode, syncToBackend]);

  const refreshQuote = useCallback(() => {
    setCurrentQuote(getRandomQuote());
  }, []);

  const addUrgentMessage = useCallback((message: UrgentMessageRequest) => {
    console.log('[MindfulnessContext] addUrgentMessage called:', message);
    setUrgentMessages(prev => {
      if (prev.some(m => m.messageId === message.messageId)) {
        console.log('[MindfulnessContext] Message already exists, skipping');
        return prev;
      }
      console.log('[MindfulnessContext] Adding new urgent message. Previous count:', prev.length);
      const newMessages = [...prev, message];
      console.log('[MindfulnessContext] New urgent messages:', newMessages);
      return newMessages;
    });
  }, []);

  const dismissUrgentMessage = useCallback((messageId: string) => {
    setUrgentMessages(prev => prev.filter(m => m.messageId !== messageId));
  }, []);

  const clearUrgentMessages = useCallback(() => {
    setUrgentMessages([]);
  }, []);

  const isChannelAccessible = useCallback((channelId: string) => {
    switch (focusMode) {
      case 'deep-work':
        return false; // No channels accessible in deep work
      case 'focus':
        return focusChannelIds.includes(channelId);
      case 'collaboration':
      default:
        return true;
    }
  }, [focusMode, focusChannelIds]);

  const shouldShowNotification = useCallback((isMention: boolean, channelId?: string) => {
    switch (focusMode) {
      case 'deep-work':
        return isMention; // Only mentions in deep work
      case 'focus':
        // In focus mode, only show notifications from selected channels
        if (channelId && !focusChannelIds.includes(channelId)) {
          return false;
        }
        return true;
      case 'collaboration':
      default:
        return true;
    }
  }, [focusMode, focusChannelIds]);

  // Sync from SignalR when receiving UserFocusModeChanged event
  // This allows focus mode changes from other devices to reflect locally
  const syncFromSignalR = useCallback((mode: FocusModeType, activatedAt: string | null) => {
    console.log('[MindfulnessContext] Syncing from SignalR:', { mode, activatedAt });
    
    // Don't trigger backend sync - this came FROM the backend via SignalR
    setFocusModeState(mode);
    setFocusConfig({
      mode,
      activatedAt: activatedAt || undefined,
    });
    
    // Reset channels and urgent messages when syncing to collaboration
    if (mode === 'collaboration') {
      setFocusChannelIdsState([]);
      setUrgentMessages([]);
    }
    
    // Refresh quote if entering deep work
    if (mode === 'deep-work') {
      setCurrentQuote(getRandomQuote());
      setUrgentMessages([]);
    }
  }, []);

  const value: MindfulnessContextType = {
    focusMode,
    focusConfig,
    setFocusMode,
    exitFocusMode,
    focusChannelIds,
    setFocusChannelIds,
    isDeepWorkActive: focusMode === 'deep-work',
    currentQuote,
    refreshQuote,
    urgentMessages,
    addUrgentMessage,
    dismissUrgentMessage,
    clearUrgentMessages,
    isModeSelectorOpen,
    setModeSelectorOpen,
    isFocusSetupOpen,
    setFocusSetupOpen,
    showFocusSummary,
    dismissFocusSummary,
    exitingFocusDuration,
    exitingFocusStartTime,
    isChannelAccessible,
    shouldShowNotification,
    syncFromSignalR,
  };

  return (
    <MindfulnessContext.Provider value={value}>
      {children}
    </MindfulnessContext.Provider>
  );
}

export function useMindfulness() {
  const context = useContext(MindfulnessContext);
  if (context === undefined) {
    throw new Error('useMindfulness must be used within a MindfulnessProvider');
  }
  return context;
}
